// frontend/src/modules/platform-billing/ManagePlanDialog.tsx
//
// Self-serve "change your plan" dialog for tenant subscribers. Surfaces the
// module-selection capability that previously only existed inside the
// trial-signup wizard (TrialWizardPage). An ACTIVE (or trial / read_only)
// subscriber can toggle modules on/off, see the live monthly estimate, and
// persist via `PATCH /api/billing/subscription` (billingApi.updateMySubscription)
// — the same endpoint the wizard uses, which the backend allows for active
// subscribers and prorates mid-cycle.
//
// Pricing revamp (2026-07-25): there is no more seat slider. Every tenant
// always pays a flat $9/mo "core" platform fee; some modules are flat-priced
// (invoicing, projects, esign), some are metered per employee (hr, monitoring,
// payroll) off LIVE counts from `GET /api/billing/seat-counts`, and the rest
// (payments, email, expenses) are included free. See billing-engine.ts /
// migrations/058_pricing_revamp.sql for the source of truth.
//
// The module catalogue + fallback prices below intentionally mirror the ones in
// TrialWizardPage (kept in sync with migrations/058_pricing_revamp.sql) so
// this dialog stands alone without editing the wizard.

import * as React from 'react';
import { toast } from 'sonner';
import { Check, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { useConfirm } from '../../components/ui/confirm-dialog';
import { cn } from '../../lib/utils';
import { api } from '../../core/api-client';
import { billingApi } from './api';
import type { ModulePrice, TenantSubscription, SeatCounts } from './types';
import {
  FALLBACK_PRICES as MARKETING_FALLBACK_PRICES,
  FALLBACK_SEAT_BASES,
  FALLBACK_INCLUDED_SEATS,
} from '../../marketing/pricingMath';

/* ────────────────────────────────────────────────────────────────────
 * Module catalogue — mirrors TrialWizardPage.MODULE_CARDS.
 * Keys MUST match migrations/058_pricing_revamp.sql.
 * ──────────────────────────────────────────────────────────────────── */
type ModuleCard = {
  key: string;
  name: string;
  blurb: string;
  /** Modules that cannot be turned off (foundational platform). */
  required?: boolean;
};

const MODULE_CARDS: ModuleCard[] = [
  { key: 'invoicing',        name: 'Invoicing',        blurb: 'Issue invoices, get paid via Stripe links, chase the late ones.' },
  { key: 'hr',               name: 'HR',               blurb: 'People, attendance, leave, and the org chart.' },
  { key: 'payroll',          name: 'Payroll',          blurb: 'Runs, payslips, bank export. Depends on HR.' },
  { key: 'payments',         name: 'Payments',         blurb: 'Process card payments and reconcile incoming money.' },
  { key: 'projects',         name: 'Projects',         blurb: 'Tasks, time entries, and margin per client.' },
  { key: 'monitoring',       name: 'Monitoring',       blurb: 'Opt-in desktop activity for billable focus tracking.' },
  { key: 'email',            name: 'Email',            blurb: 'Send notifications, reminders, and customer messages.' },
  { key: 'expenses',         name: 'Expenses',         blurb: 'Capture receipts, approve claims, reimburse staff.' },
  { key: 'esign',            name: 'E-Sign',           blurb: 'Send documents for legally binding electronic signature.' },
  { key: 'platform-billing', name: 'Platform billing', blurb: 'Your subscription and invoices live here.', required: true },
];

/** Fallback prices used only while the server catalogue is loading. Kept in
 *  sync with migrations/058_pricing_revamp.sql (+ the payroll-band migration)
 *  by re-using the marketing calculator's single source of truth
 *  (frontend/src/marketing/pricingMath.ts) rather than duplicating numbers. */
const FALLBACK_PRICES: Record<string, { base: number; seat: number; included: number }> = {
  invoicing:          { base: MARKETING_FALLBACK_PRICES.invoicing, seat: 0, included: 0 },
  hr:                 { base: 0, seat: MARKETING_FALLBACK_PRICES.hr, included: FALLBACK_INCLUDED_SEATS.hr },
  payroll:            { base: FALLBACK_SEAT_BASES.payroll, seat: MARKETING_FALLBACK_PRICES.payroll, included: FALLBACK_INCLUDED_SEATS.payroll },
  payments:           { base: 0, seat: 0, included: 0 },
  projects:           { base: MARKETING_FALLBACK_PRICES.projects, seat: 0, included: 0 },
  monitoring:         { base: 0, seat: MARKETING_FALLBACK_PRICES.monitoring, included: FALLBACK_INCLUDED_SEATS.monitoring },
  email:              { base: 0, seat: 0, included: 0 },
  expenses:           { base: 0, seat: 0, included: 0 },
  esign:              { base: MARKETING_FALLBACK_PRICES.esign, seat: 0, included: 0 },
  'platform-billing': { base: 0, seat: 0, included: 0 },
};

/** Flat $9/mo platform fee every tenant always pays. Not a toggleable module
 *  — it never appears in `tenant_modules` or MODULE_CARDS. Fallback used only
 *  while the server price catalogue is loading. */
const CORE_FALLBACK_PRICE = MARKETING_FALLBACK_PRICES.core;

/** module_key → the seat vocabulary noun used in per-employee pricing copy.
 *  Mirrors billing-engine.ts's SEAT_NOUNS + the seat-counts response shape.
 *  Payroll bills active headcount, not who got actually paid — plain
 *  "employee" (2026-07-29). */
const SEAT_LABEL: Record<string, string> = {
  hr: 'clock-in employee',
  monitoring: 'monitored employee',
  payroll: 'employee',
};

/** module_key → which field of SeatCounts prices its metered seats. */
const SEAT_COUNT_FIELD: Record<string, keyof SeatCounts> = {
  hr: 'clock_in',
  monitoring: 'monitored',
  payroll: 'employees',
};

/** Whole-dollar amounts render without a decimal; fractional ones keep two
 *  places (e.g. Payroll's $1.50/employee overage rate). */
function fmtAmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/** Human label for a module key — used by BillingPage's summary chip row. */
export function moduleLabel(key: string): string {
  return MODULE_CARDS.find((m) => m.key === key)?.name ?? key;
}

/** Which subscription statuses may self-serve edit their plan. Mirrors the
 *  backend whitelist in api/modules/platform-billing/routes.ts (PATCH). */
export const PLAN_EDITABLE_STATUSES: TenantSubscription['status'][] = ['trial', 'active', 'read_only'];

export function ManagePlanDialog({
  open,
  onOpenChange,
  subscription,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: TenantSubscription;
  onSaved: () => void;
}) {
  const confirm = useConfirm();
  const [prices, setPrices] = React.useState<ModulePrice[] | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [saving, setSaving] = React.useState(false);
  const [seatCounts, setSeatCounts] = React.useState<SeatCounts | null>(null);
  const [seatCountsLoading, setSeatCountsLoading] = React.useState(true);
  const [seatCountsError, setSeatCountsError] = React.useState(false);
  // Whether Monitoring was ON in the SAVED plan when the dialog opened — the
  // context `seatCounts` was computed under. `seatCounts.clock_in` /
  // `.monitored` are disjoint under THAT saved context; if the user toggles
  // Monitoring inside the dialog without reloading, the raw counts go stale
  // (see I3 review finding). Reconstructed below via `derivedSeatCounts`.
  const [savedMonitoringWasOn, setSavedMonitoringWasOn] = React.useState(false);

  // Load the tenant-visible price catalogue once (best-effort — fallbacks fill in).
  React.useEffect(() => {
    let cancelled = false;
    billingApi.listMyModulePrices()
      .then((p) => { if (!cancelled) setPrices(p); })
      .catch(() => { /* silent — FALLBACK_PRICES kick in */ });
    return () => { cancelled = true; };
  }, []);

  // Load LIVE seat counts every time the dialog opens — per-employee lines
  // are always priced from real usage, never a manually-entered number.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSeatCountsLoading(true);
    setSeatCountsError(false);
    billingApi.seatCounts()
      .then((c) => { if (!cancelled) { setSeatCounts(c); setSeatCountsLoading(false); } })
      .catch(() => { if (!cancelled) { setSeatCountsError(true); setSeatCountsLoading(false); } });
    return () => { cancelled = true; };
  }, [open]);

  // (Re)seed the form every time the dialog opens, so it always reflects the
  // current plan (and discards abandoned edits). Module selection is seeded
  // from GET /api/tenant/enabled-modules — the LIVE tenant_modules state — not
  // the subscription's enabled_module_keys snapshot, which can be stale when a
  // super-admin has overridden the tenant's modules (saving from the snapshot
  // would silently clobber those overrides). The snapshot remains the fallback
  // if the fetch fails.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const seed = (keys: string[]) => {
      const next = new Set<string>(keys);
      for (const m of MODULE_CARDS) if (m.required) next.add(m.key);
      setSelected(next);
      setSavedMonitoringWasOn(next.has('monitoring'));
    };
    api<{ modules: string[] }>('/api/tenant/enabled-modules')
      .then((r) => { if (!cancelled) seed(r.modules); })
      .catch(() => {
        if (cancelled) return;
        seed(subscription.enabled_module_keys && subscription.enabled_module_keys.length > 0
          ? subscription.enabled_module_keys
          : MODULE_CARDS.filter((m) => m.key !== 'platform-billing').map((m) => m.key));
      });
    return () => { cancelled = true; };
  }, [open, subscription]);

  const priceFor = React.useCallback((key: string): { base: number; seat: number; included: number } => {
    if (prices) {
      const row = prices.find((p) => p.module_key === key);
      if (row) {
        return {
          base: Number(row.amount_per_month) || 0,
          seat: Number(row.seat_amount_per_month ?? 0) || 0,
          included: Number(row.included_seats ?? FALLBACK_PRICES[key]?.included ?? 0) || 0,
        };
      }
    }
    return FALLBACK_PRICES[key] ?? { base: 0, seat: 0, included: 0 };
  }, [prices]);

  /** The always-on $9/mo core platform fee — live from the server catalogue's
   *  `core` price row when available, else the fallback. */
  const corePrice = React.useMemo(() => {
    const row = prices?.find((p) => p.module_key === 'core');
    return row ? Number(row.amount_per_month) || 0 : CORE_FALLBACK_PRICE;
  }, [prices]);

  const currency = subscription.billing_currency ?? 'USD';

  const selectedMonitoringOn = selected.has('monitoring');

  /**
   * Reconstructs disjoint clock-in/monitored counts under the CURRENTLY
   * selected Monitoring toggle state, from the server's counts (which are
   * only valid under the SAVED state). Server `clock_in` already excludes
   * `monitored` when Monitoring was on at save time; when it was off, every
   * active employee lands in `clock_in` and `monitored` is 0. So:
   *   totalActives = savedMonitoringWasOn ? clock_in + monitored : clock_in
   * and the displayed split re-partitions totalActives by the SELECTED state.
   */
  const derivedSeatCounts = React.useMemo(() => {
    if (!seatCounts) return null;
    const totalActives = savedMonitoringWasOn
      ? seatCounts.clock_in + seatCounts.monitored
      : seatCounts.clock_in;
    return {
      clock_in: selectedMonitoringOn ? Math.max(0, totalActives - seatCounts.monitored) : totalActives,
      monitored: selectedMonitoringOn ? seatCounts.monitored : 0,
      employees: seatCounts.employees,
    };
  }, [seatCounts, savedMonitoringWasOn, selectedMonitoringOn]);

  /**
   * Live monthly estimate. Core is always included. Each selected module
   * contributes its flat base (Payroll's base bundles an included employee
   * allowance) plus — for hr/monitoring/payroll — its per-employee rate ×
   * however much of the matching LIVE seat count is beyond that module's
   * included allowance (0 for hr/monitoring, so this is a no-op for them).
   * If the seat-counts fetch failed, `metered` is 0 and the caller shows a
   * flats-only estimate (still including Payroll's known base fee) with an
   * explanatory note instead of a possibly-wrong number.
   */
  const total = React.useMemo(() => {
    let flats = 0;
    let metered = 0;
    selected.forEach((key) => {
      if (key === 'platform-billing') return;
      const p = priceFor(key);
      flats += p.base;
      if (p.seat > 0 && derivedSeatCounts) {
        const field = SEAT_COUNT_FIELD[key];
        const count = field ? derivedSeatCounts[field] : 0;
        const billable = Math.max(0, count - p.included);
        metered += p.seat * billable;
      }
    });
    return {
      core: corePrice,
      flats,
      metered,
      total: corePrice + flats + metered,
    };
  }, [selected, priceFor, corePrice, derivedSeatCounts]);

  function toggle(key: string) {
    const card = MODULE_CARDS.find((m) => m.key === key);
    if (card?.required) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const prorates = subscription.status === 'active';

  async function save() {
    const ok = await confirm({
      title: 'Update your plan?',
      description: prorates
        ? 'Module changes take effect immediately and are prorated on your next invoice.'
        : 'Your enabled modules will be updated. Billing begins after your trial.',
      confirmLabel: 'Save changes',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await billingApi.updateMySubscription({
        enabledModuleKeys: Array.from(selected),
      });
      toast.success('Plan updated', {
        description: prorates ? 'Changes are reflected on your next invoice.' : undefined,
      });
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Could not update your plan', { description: err?.message ?? 'Try again in a moment.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage plan &amp; modules</DialogTitle>
          <DialogDescription>
            Turn modules on or off. Your bill is a flat $9/mo core fee plus the
            modules you keep — some are flat-priced, some bill per employee from
            your live counts.
          </DialogDescription>
        </DialogHeader>

        {/* Module grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {MODULE_CARDS.map((m) => {
            const price = priceFor(m.key);
            const isOn = selected.has(m.key);
            const isFree = price.base === 0 && price.seat === 0;
            return (
              <button
                type="button"
                key={m.key}
                onClick={() => toggle(m.key)}
                disabled={m.required}
                aria-pressed={isOn}
                className={cn(
                  'relative text-left rounded-2xl p-3.5 ring-1 transition-all',
                  isOn
                    ? 'bg-coral/[0.06] ring-coral/40'
                    : 'bg-card ring-border/50 hover:ring-border',
                  m.required && 'opacity-90 cursor-default',
                )}
              >
                <div className="flex items-start justify-between gap-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="text-[13.5px] font-semibold text-foreground truncate">{m.name}</h4>
                      {m.required && (
                        <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                          <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
                          Core
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[11.5px] text-muted-foreground leading-snug">{m.blurb}</p>
                  </div>
                  <span
                    aria-hidden
                    className={cn(
                      'shrink-0 h-5 w-5 rounded-full inline-flex items-center justify-center transition-colors',
                      isOn ? 'bg-coral text-white' : 'bg-muted ring-1 ring-border',
                    )}
                  >
                    {isOn && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                </div>
                <div className="mt-2.5 flex items-baseline gap-1.5 flex-wrap">
                  {isFree ? (
                    <span className="text-[11.5px] font-bold text-muted-foreground tracking-wide">Included</span>
                  ) : (
                    <>
                      {price.base > 0 && (
                        <>
                          <span className="text-[14px] font-bold tabular-nums text-foreground">
                            {fmtAmt(price.base)} {currency}
                          </span>
                          <span className="text-[11px] text-muted-foreground">/ mo flat</span>
                        </>
                      )}
                      {price.seat > 0 && (
                        <span className="ml-1 text-[11.5px] font-semibold text-coral-700 dark:text-coral-300 tabular-nums">
                          + {fmtAmt(price.seat)} / {SEAT_LABEL[m.key] ?? 'employee'}
                          {price.included > 0 ? ` beyond ${price.included}` : ''}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Live total */}
        <div className="mt-1 rounded-2xl bg-ink-900 text-white ring-1 ring-white/10 p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-white/55">
            Estimated monthly bill
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="font-bold text-[28px] leading-none tabular-nums">{total.total.toFixed(2)}</span>
            <span className="text-[13px] text-white/65 font-medium">{currency}</span>
            <span className="text-[11.5px] text-white/50 ml-1">/ mo</span>
          </div>
          {seatCountsLoading ? (
            <div className="mt-1.5 text-[11px] text-white/55">Loading your employee counts…</div>
          ) : seatCountsError ? (
            <div className="mt-1.5 text-[11px] text-white/55">
              {(total.core + total.flats).toFixed(2)} {currency} in flat fees shown — per-employee
              charges are billed from your live employee counts and couldn't be loaded right now.
            </div>
          ) : (
            <div className="mt-1.5 text-[11px] text-white/55 tabular-nums">
              {total.core.toFixed(2)} core + {total.flats.toFixed(2)} module flats + {total.metered.toFixed(2)} per-employee
            </div>
          )}
        </div>

        {/* How your bill is calculated */}
        <div className="rounded-2xl bg-card ring-1 ring-border/50 p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            How your bill is calculated
          </div>
          <ul className="mt-2 space-y-1 text-[12px] text-muted-foreground leading-relaxed">
            <li><span className="font-semibold text-foreground">Clock-in employee</span> — any active employee not billed as monitored (when Monitoring is off, that's all active employees).</li>
            <li><span className="font-semibold text-foreground">Monitored employee</span> — an active employee running the desktop agent.</li>
            <li><span className="font-semibold text-foreground">Payroll</span> — a flat base fee covering your first {priceFor('payroll').included} active employees, then a per-employee rate for anyone beyond that. Billing is in advance, priced off your workspace on the charge date.</li>
          </ul>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
